import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon, ArrowRightIcon} from '@components/ui/icon'
import {DataTable} from '@components/commons/data-table'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {ListingTableCellInfo} from '@features/home/components/listing-table-cell-info'
import {generatePath, useLocation} from 'react-router-dom'
import {routes} from '@utilities/constants/routes.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {AssignmentsURLParams} from '@features/home/types.ts'
import {useHomeAssignments} from '@features/home/queries/useHomeAssignments.ts'
import {HomeTableEmptyState} from '@features/home/components/home-table-empty-state'
import {HomeTableEmptySearch} from '@features/home/components/home-table-empty-search'
import {useMediaQuery} from '@hooks/use-media-query'
import {MobileTable} from '@components/commons/mobile-table.tsx'
import {PrimaryMobileTableRow, TableAreaItem, TableDateItems} from '@features/home/components/home-atoms'
import {TABLE_ELEMENT_LIMIT} from '@features/home/utils.tsx'
import {rejectedReasonsToTranslationKey} from '@features/assignment/components/assignment-descriptions/needs-more-assets-description'
import {Tooltip} from '@components/ui/tooltip'
import {ButtonLink} from '@components/commons/button-link'

const ReasonBodyCellTextClassName =
    'block max-w-[300px] max-h-full whitespace-normal text-left text-sm text-neutral-950 overflow-hidden text-ellipsis md:[display:-webkit-box] md:max-w-none md:max-h-10 md:[-webkit-line-clamp:2] md:[-webkit-box-orient:vertical]'

export const NeedMoreAssetsTable = ({onClearSearch}: {onClearSearch: () => void}) => {
    const {t} = useTranslation()
    const {search} = useLocation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const assignmentsQuery = useHomeAssignments(searchParams)
    const isLarge = useMediaQuery({breakpoint: 'md'})

    return (
        <div className="flex flex-col gap-4 w-full items-stretch">
            <p>{t('home:table:titles:assignments_with_deliverable_issue')}</p>
            {assignmentsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : assignmentsQuery.data?.data.length == 0 && !searchParams.search ? (
                <HomeTableEmptyState />
            ) : !isLarge ? (
                <MobileTable
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    emptyStateComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    rows={[
                        {
                            id: 'listing',
                            cell: ({dataItem}) => (
                                <PrimaryMobileTableRow>
                                    <ListingTableCellInfo item={dataItem} />
                                    <ButtonLink
                                        to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        })}
                                        size={'sm'}
                                        variant={'tertiary'}
                                    >
                                        <ArrowRightIcon />
                                    </ButtonLink>
                                </PrimaryMobileTableRow>
                            ),
                            header: () => <></>
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: () => <TableBodyCellText variant={'normal'}>{t('commons:area')}</TableBodyCellText>,
                            id: 'area'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: () => (
                                <TableBodyCellText variant={'normal'}>
                                    {t('dashboard:calendar:table:host')}
                                </TableBodyCellText>
                            ),
                            id: 'host'
                        },
                        {
                            cell: ({dataItem}) =>
                                dataItem.assets_rejected_at ? (
                                    <TableDateItems date={dataItem.assets_rejected_at} />
                                ) : (
                                    '-'
                                ),
                            header: () => (
                                <TableBodyCellText>{t('home:table:need_more_assets_date')}</TableBodyCellText>
                            ),
                            id: 'need_more_assets_date'
                        },
                        {
                            cell: ({dataItem}) => (
                                <span className={ReasonBodyCellTextClassName}>
                                    {dataItem.assets_rejected_reason
                                        ? t(rejectedReasonsToTranslationKey[dataItem.assets_rejected_reason])
                                        : '-'}
                                </span>
                            ),
                            header: () => <TableBodyCellText>{t('home:table:reason')}</TableBodyCellText>,
                            id: 'reason'
                        }
                    ]}
                />
            ) : (
                <DataTable
                    emptyComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    columnPinning={{right: ['cta'], left: ['listing']}}
                    order={{
                        orderBy: assignmentsQuery.sort.sortName ?? undefined,
                        orderDirection: assignmentsQuery.sort.direction ?? undefined
                    }}
                    setOrder={nextOrder =>
                        assignmentsQuery.sort.onSort(nextOrder?.orderBy ?? null, nextOrder?.orderDirection ?? null)
                    }
                    columns={[
                        {
                            cell: ({dataItem}) => <ListingTableCellInfo item={dataItem} />,
                            header: t('commons:listing').toUpperCase(),
                            id: 'listing',
                            size: 185,
                            sortKey: 'title'
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: t('commons:area').toUpperCase(),
                            id: 'area',
                            size: 260,
                            sortKey: 'address'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: t('dashboard:calendar:table:host').toUpperCase(),
                            id: 'host',
                            size: 185,
                            sortKey: 'first_name'
                        },
                        {
                            cell: ({dataItem}) =>
                                dataItem.assets_rejected_at ? (
                                    <TableDateItems date={dataItem.assets_rejected_at} />
                                ) : (
                                    '-'
                                ),
                            header: t('home:table:need_more_assets_date').toUpperCase(),
                            id: 'need_more_assets_date',
                            size: 240,
                            sortKey: 'assets_rejected_at'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>
                                    <Tooltip
                                        contentProps={{className: 'max-w-75'}}
                                        content={
                                            dataItem.assets_rejected_reason &&
                                            t(rejectedReasonsToTranslationKey[dataItem.assets_rejected_reason])
                                        }
                                    >
                                        <span className={ReasonBodyCellTextClassName}>
                                            {dataItem.assets_rejected_reason
                                                ? t(rejectedReasonsToTranslationKey[dataItem.assets_rejected_reason])
                                                : '-'}
                                        </span>
                                    </Tooltip>
                                </TableBodyCellText>
                            ),
                            header: t('home:table:reason').toUpperCase(),
                            id: 'reason',
                            size: 240,
                            sortKey: 'declined_reason_notes'
                        },
                        {
                            cell: ({dataItem}) => (
                                <ButtonLink
                                    to={{
                                        pathname: generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        }),
                                        search
                                    }}
                                    size={'sm'}
                                    variant={'tertiary'}
                                >
                                    {t('commons:open')}
                                </ButtonLink>
                            ),
                            header: <></>,
                            id: 'cta',
                            size: 80
                        }
                    ]}
                />
            )}
        </div>
    )
}
